import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Search, ArrowUpRight, Newspaper, MapPin, Package, Send, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Link } from "react-router-dom";
import EarthquakeMap from "@/components/EarthquakeMap";
import Gallery from "@/components/Gallery";

/* Number formatter */
const fmt = (v) => (typeof v === "number" ? v.toLocaleString("id-ID") : (v ?? "—"));
const rupiahFull = (n) => (n ? "Rp " + Number(n).toLocaleString("id-ID") : "Rp 0");
const rupiah = (n) => {
  if (!n) return "Rp 0";
  const num = Number(n);
  if (num >= 1_000_000_000) return "Rp " + (num / 1_000_000_000).toFixed(1).replace(".", ",") + " M";
  if (num >= 1_000_000) return "Rp " + (num / 1_000_000).toFixed(1).replace(".", ",") + " jt";
  return "Rp " + num.toLocaleString("id-ID");
};

function StatUnit({ label, value, tid, big = false, title }) {
  return (
    <div data-testid={tid} className="py-3 min-w-0" title={title}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#141414]/60 min-h-[2.8em] leading-snug">{label}</div>
      <div className={`font-display leading-none mt-2 whitespace-nowrap tabular-nums overflow-hidden ${big ? "text-2xl sm:text-2xl md:text-3xl lg:text-3xl xl:text-4xl" : "text-lg sm:text-xl md:text-2xl lg:text-2xl"}`}>{value}</div>
    </div>
  );
}

function SnippetCard({ to, kicker, title, children, tid }) {
  return (
    <Link to={to} data-testid={tid} className="group block flat-border rounded-2xl bg-white p-5 md:p-6 hover:border-[#670003] transition-colors">
      <div className="flex items-start justify-between mb-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#670003]">{kicker}</div>
        <ArrowUpRight className="w-5 h-5 opacity-60 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
      </div>
      <h3 data-testid={tid ? `${tid}-title` : undefined} className="font-display text-xl md:text-2xl mb-3">{title}</h3>
      {children}
    </Link>
  );
}

export default function Dashboard() {
  const { t, settings, lang } = useApp();
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [people, setPeople] = useState([]);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/news").then((r) => setNews(r.data.items || []));
    api.get("/beneficiaries").then((r) => setPeople(r.data.slice(0, 3)));
    api.get("/resources").then((r) => setResources(r.data.slice(0, 4)));
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const h = setTimeout(() => {
      api.get(`/beneficiaries?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data.slice(0, 5)));
    }, 250);
    return () => clearTimeout(h);
  }, [q]);

  const primary = settings?.color_primary || "#670003";
  const ink = settings?.color_ink || "#141414";
  const cream = settings?.color_background || "#fff0d2";
  const barColor = (i) => (i === 0 ? primary : ink);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      {/* Hero */}
      <section className="mb-10">
        <div className="inline-flex items-center gap-2 flat-border rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--maroon)] animate-pulse" /> {settings?.[`hero_kicker_${lang}`] || "Flores 7.7 · Live"}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl">
          {settings?.[`hero_title_${lang}`] || t.dashboard.title}
        </h1>
        <p className="mt-4 text-sm sm:text-base md:text-lg max-w-2xl text-[#141414]/70">{settings?.[`hero_subtitle_${lang}`] || t.dashboard.subtitle}</p>

        {/* Search */}
        <div className="mt-8 relative max-w-2xl">
          <div className="flat-border rounded-full flex items-center px-5 py-3 gap-3 bg-white">
            <Search className="w-4 h-4" strokeWidth={2} />
            <input
              data-testid="dashboard-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.search.placeholder}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#141414]/40"
            />
          </div>
          {results.length > 0 && (
            <div className="absolute z-30 mt-2 w-full bg-white flat-border rounded-2xl overflow-hidden">
              {results.map((r) => (
                <Link
                  to="/beneficiaries"
                  key={r.id}
                  data-testid={`search-result-${r.id}`}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-[#fff0d2] border-b border-[#141414]/10 last:border-b-0"
                  onClick={() => setResults([])}
                >
                  <MapPin className="w-4 h-4 mt-1 shrink-0" strokeWidth={2} />
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{r.pic_name} · {r.kota}, {r.kabupaten}</div>
                    <div className="text-xs text-[#141414]/60 truncate">{(r.needs || []).map(n => n.name || n).join(", ")}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Infographic row — minimalist */}
      <section className="mb-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 md:gap-x-8 gap-y-6 lg:divide-x lg:divide-y-0 divide-y divide-[#141414]/15">
          <div className="lg:pl-0 pt-0"><StatUnit tid="stat-people-helped" label={t.dashboard.stats.people_helped} value={fmt(stats?.people_helped)} big /></div>
          <div className="lg:pl-8 pt-0"><StatUnit tid="stat-people-total" label={t.dashboard.stats.people_total} value={fmt(stats?.people_total)} big /></div>
          <div className="lg:pl-8 pt-4 lg:pt-0"><StatUnit tid="stat-fulfillment" label={t.dashboard.stats.fulfillment_rate} value={`${fmt(stats?.fulfillment_rate ?? 0)}%`} big /></div>
          <div className="lg:pl-8 pt-4 lg:pt-0 min-w-0"><StatUnit tid="stat-money" label={t.dashboard.stats.money_raised} value={rupiah(stats?.money_raised)} title={rupiahFull(stats?.money_raised)} big /></div>
        </div>
        <div className="hair my-8" />
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-6 gap-y-4">
          <StatUnit tid="stat-kabupaten" label={t.dashboard.stats.kabupaten} value={fmt(stats?.kabupaten_count)} />
          <StatUnit tid="stat-kota" label={t.dashboard.stats.kota} value={fmt(stats?.kota_count)} />
          <StatUnit tid="stat-donors" label={t.dashboard.stats.donors} value={fmt(stats?.donors_total)} />
          <StatUnit tid="stat-goods" label={t.dashboard.stats.goods_pledges} value={fmt(stats?.goods_pledges)} />
          <StatUnit tid="stat-items" label={t.dashboard.stats.items} value={fmt(stats?.items_total)} />
          <StatUnit tid="stat-vendors" label={t.dashboard.stats.vendors} value={fmt(stats?.vendors_total)} />
          <StatUnit tid="stat-transport" label={t.dashboard.stats.transport} value={fmt(stats?.transport_total)} />
          <StatUnit tid="stat-drop-points" label={t.dashboard.stats.drop_points} value={fmt(stats?.drop_points_total)} />
        </div>
      </section>

      {/* Charts row */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        <div className="lg:col-span-2 flat-border rounded-2xl bg-white p-6" data-testid="chart-by-region">
          <div className="flex items-baseline justify-between mb-4">
            <div className="font-display text-xl">{t.dashboard.byRegion}</div>
            <div className="text-xs text-[#141414]/60">jumlah orang</div>
          </div>
          {stats?.by_region?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.by_region} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="region" tick={{ fontSize: 10, fill: ink }} axisLine={{ stroke: ink }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: ink }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: cream }} contentStyle={{ borderRadius: 8, border: `1px solid ${ink}`, fontFamily: "Space Grotesk", fontSize: 12 }} />
                <Bar dataKey="people" radius={[3, 3, 0, 0]}>
                  {stats.by_region.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[220px] flex items-center justify-center text-sm text-[#141414]/50">No data yet</div>}
        </div>

        <div className="flat-border rounded-2xl bg-white p-6" data-testid="chart-top-needs">
          <div className="flex items-baseline justify-between mb-4">
            <div className="font-display text-xl">{t.dashboard.topNeeds}</div>
            <div className="text-xs text-[#141414]/60">frekuensi</div>
          </div>
          <div className="space-y-3">
            {(stats?.top_needs || []).map((n, i) => {
              const max = stats.top_needs[0]?.count || 1;
              const pct = (n.count / max) * 100;
              return (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold">{n.name}</span>
                    <span className="text-[#141414]/60">{n.count}</span>
                  </div>
                  <div className="h-1.5 progress-track">
                    <div className="h-full" style={{ width: `${pct}%`, backgroundColor: primary }} />
                  </div>
                </div>
              );
            })}
            {(!stats?.top_needs || stats.top_needs.length === 0) && (
              <div className="text-sm text-[#141414]/50">No data yet</div>
            )}
          </div>
        </div>
      </section>

      {/* Snippet cards linking to each page */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        <SnippetCard to="/beneficiaries" tid="snippet-people" kicker={t.nav.beneficiaries} title={settings?.[`snippet_people_title_${lang}`] || t.dashboard.snippetPeople}>
          <ul className="divide-y divide-[#141414]/10">
            {people.map((b) => (
              <li key={b.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{b.pic_name}</div>
                  <div className="text-[11px] text-[#141414]/60 truncate">{b.kota}, {b.kabupaten}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-bold">{Math.round(b.progress || 0)}%</div>
                  <div className="w-16 h-1 progress-track mt-1">
                    <div className="h-full bg-[#670003]" style={{ width: `${b.progress || 0}%` }} />
                  </div>
                </div>
              </li>
            ))}
            {people.length === 0 && <li className="py-2 text-sm text-[#141414]/50">—</li>}
          </ul>
          <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#670003]">
            <Users className="w-3.5 h-3.5" strokeWidth={2} /> {t.dashboard.viewAll}
          </div>
        </SnippetCard>

        <SnippetCard to="/resources" tid="snippet-resources" kicker={t.nav.resources} title={settings?.[`snippet_resources_title_${lang}`] || t.dashboard.snippetResources}>
          <ul className="divide-y divide-[#141414]/10">
            {resources.map((r) => (
              <li key={r.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{r.name}</div>
                  <div className="text-[11px] text-[#141414]/60 truncate">{t.resources.tabs[r.category] || r.category}{r.location ? ` · ${r.location}` : ""}</div>
                </div>
                <div className="text-right shrink-0 min-w-0">
                  {r.quantity_total > 0 ? (
                    <>
                      <div className="text-xs font-mono"><span className="font-bold">{r.quantity_delivered}</span>/<span className="text-[#141414]/60">{r.quantity_total}</span></div>
                      <div className="w-16 h-1 progress-track mt-1">
                        <div className="h-full bg-[color:var(--maroon)]" style={{ width: `${r.progress_pct || 0}%` }} />
                      </div>
                    </>
                  ) : (
                    <div className="text-xs font-mono text-[#141414]/80">{r.quantity || "—"}</div>
                  )}
                </div>
              </li>
            ))}
            {resources.length === 0 && <li className="py-2 text-sm text-[#141414]/50">—</li>}
          </ul>
          <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[color:var(--maroon)]">
            <Package className="w-3.5 h-3.5" strokeWidth={2} /> {t.dashboard.viewAll}
          </div>
        </SnippetCard>

        <SnippetCard to="/submit" tid="snippet-submit" kicker={t.nav.submit} title={settings?.[`snippet_submit_title_${lang}`] || t.dashboard.snippetSubmit}>
          <p className="text-sm text-[#141414]/80 leading-relaxed">{t.dashboard.snippetSubmitBody}</p>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="flat-border rounded-xl px-3 py-3">
              <div className="text-[10px] uppercase tracking-wider text-[#141414]/60">{t.submit.tabs.receiver}</div>
              <div className="text-sm font-semibold mt-1">→</div>
            </div>
            <div className="rounded-xl px-3 py-3 bg-[#670003] text-[#fff0d2]">
              <div className="text-[10px] uppercase tracking-wider opacity-80">{t.submit.tabs.donor}</div>
              <div className="text-sm font-semibold mt-1">→</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#670003]">
            <Send className="w-3.5 h-3.5" strokeWidth={2} /> {t.dashboard.viewAll}
          </div>
        </SnippetCard>
      </section>

      {/* Documentation gallery (editable via admin) */}
      <Gallery />

      {/* Live earthquake map (BMKG · USGS · Flores area) — placed after program deliverables, before news */}
      <EarthquakeMap />

      {/* News */}
      <section className="flat-border rounded-2xl bg-white p-6" data-testid="news-panel">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="w-4 h-4" strokeWidth={2} />
          <div className="font-display text-xl">{t.dashboard.news}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[420px] overflow-y-auto">
          {news.length === 0 && <div className="text-sm text-[#141414]/60">{t.dashboard.newsEmpty}</div>}
          {news.slice(0, 9).map((n, i) => (
            <a key={i} href={n.link} target="_blank" rel="noopener noreferrer" data-testid={`news-item-${i}`}
               className="flat-border rounded-xl p-3 hover:bg-[#fff0d2] transition-colors">
              <div className="text-[10px] uppercase font-semibold text-[#670003] tracking-[0.15em]">{n.source || "News"}</div>
              <div className="font-semibold text-sm mt-1.5 leading-snug line-clamp-3">{n.title}</div>
              <div className="text-[10px] text-[#141414]/50 mt-2">{n.published}</div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
